<template>
    <div class="flex truncate flex-col">
        <label v-if="label" class="block text-sm font-medium text-gray-700 mb-2" v-text="label"></label>
        <div class="flex flex-col p-2 border-2" ref="dropper" :class="dropHovering?'border-blue-600':'border-transparent'">
            <div class="w-auto h-auto p-0 overflow-x-auto mb-0 pb-2 overflow-y-hidden whitespace-nowrap"
                 v-if="files.length">
                <file-inline-preview v-for="(file,index) in files" :key="index" :file="file"
                                     @mouseover="highlighted=file.id"
                                     @mouseleave="highlighted=false"
                                     @click="download(file)"
                                     :highlighted="file.id===highlighted"
                                     :ref="'filePreview'+file.id"></file-inline-preview>
            </div>
            <div class="flex items-end flex-wrap truncate">
                <div class="flex-1 flex rounded-md shadow-none mx-0 mt-0 mb-0 truncate">
                    <button @click.prevent="$refs.input.click()"
                            v-if="!readonly && (typeof attachUrl === 'function')"
                            class="z-10 -mr-px relative inline-flex flex-shrink-0 items-center space-x-3 px-4 py-2 border text-sm font-medium rounded-l-md text-gray-700 bg-gray-50 hover:bg-gray-100 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                            :class="(noFileList?'rounded-r-md ':'')+dynamicClasses">
                        <i class="fas fa-upload text-sm text-gray-400"></i>
                        <div v-text="'Attach file'+(this.single?'':'(s)')"></div>
                    </button>
                    <div
                        class="relative rounded-r-md flex-grow border sm:text-sm px-1 py-1 flex items-center flex-wrap truncate"
                        v-if="!noFileList"
                    >
                        <file-inline-link v-for="(file,index) in files" :key="index" :file="file"
                                          :readonly="readonly || (typeof detachUrl !== 'function')"
                                          @remove="removeFile(file)"
                                          @mouseover="highlighted=file.id"
                                          @mouseleave="highlighted=false"
                                          @click="download(file)"
                                          :highlighted="file.id===highlighted"
                                          :ref="'fileLink'+file.id"></file-inline-link>
                        <div v-if="!files.length" class="py-3 pl-2">No media attached</div>
                    </div>
                    <input
                        ref="input"
                        type="file"
                        class="hidden"
                        :name="name"
                        :multiple="!this.single"
                        @change="onFilesChange"
                    >
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import {toFormData, uuid} from "../../../utils";
import FileInlinePreview from "./FileInlinePreview.vue";
import FileInlineLink from "./FileInlineLink.vue";
import Promise from "lodash/_Promise";
import User from "../../../services/User";

export default {
    components: {FileInlineLink, FileInlinePreview},
    props: {
        maxFilesize: {
            type: Number,
            default: 104857600
        },
        filesystemId: null,
        currentFiles: Function,
        label: String,
        name: String,
        error: String,
        warning: String,
        info: String,
        preview: Boolean,
        single: {
            type: Boolean,
            default: false,
        },
        noFileList: {
            type: Boolean,
            default: false,
        },
        current: String | Array,
        placeholder: {
            type: String,
            default: 'No file chosen...',
        },
        readonly: {type: Boolean, default: false},
        attachUrl: Function,
        detachUrl: Function,
        downloadUrl: Function,
    },
    data() {
        return {
            files: [],
            fileKeys: {},
            highlighted: false,
            dropHovering: false,
        }
    },
    computed: {
        dynamicClasses() {
            if (this.error) return 'border-red-300'
            if (this.disabled) return 'border-gray-300 text-gray-500 bg-gray-50'
            return 'border-gray-300 placeholder-gray-400'
        },
    },
    methods: {
        processDrop(file) {
            console.log(file)
        },
        onFilesChange(event) {
            event.target.files.forEach(file => {
                if (this.single) {
                    this.files = [{
                        'status': 'queued',
                        'id': uuid(),
                        'file': file,
                        'name': file.name
                    }]
                } else {
                    this.files.push({
                        'status': 'queued',
                        'id': uuid(),
                        'file': file,
                        'name': file.name
                    })
                }
            })
            this.$nextTick(() => {
                this.uploadFiles()
            })
        },
        async uploadFiles() {
            let p = [];
            let files = this.files.filter((f) => f.status === 'queued').slice(0, 5);
            files.forEach(file => {
                if (file.file.size > this.maxFilesize) { // 100mb max upload
                    file.status = 'failed'
                    file.errorMessage = 'Maximum file size of ' + Math.round(this.maxFilesize / (1024 * 1024)) + 'mb'
                } else {
                    file.status = 'uploading'
                    p.push(Http.put(this.attachUrl(file.id), toFormData({'media': file.file})));
                }
                this.reRenderFile(file)
            })
            Promise.all(p).then((values) => {
                values.forEach((value, key) => {
                    let ob = this.files.find((i) => i.id === files[key].id)
                    if ((ob) && (value.status === 'fulfilled')) {
                        if (value.value && (value.value.status === 'ok')) {
                            ob.status = 'uploaded'
                        } else {
                            console.log('error')
                            ob.status = 'failed'
                            ob.errorMessage = value.value.message
                        }
                        this.reRenderFile(ob)
                    }
                })
                if (this.files.filter((f) => f.status === 'queued').length) {
                    setTimeout(() => this.uploadFiles(), 10)
                } else {
                    this.updateFiles()
                }
            })
        },
        async removeFile(file) {
            Http.delete(this.detachUrl(file.id)).then((value) => {
                this.files.splice(this.files.indexOf(file), 1)
            }).catch((error) => {
                this.files.splice(this.files.indexOf(file), 1)
            })
        },
        reRenderFile(file) {
            if (this.$refs['filePreview' + file.id]) {
                this.$refs['filePreview' + file.id][0].$data.renderCount++;
                this.$refs['filePreview' + file.id][0].$forceUpdate()
            }
            if (this.$refs['fileLink' + file.id]) {
                this.$refs['fileLink' + file.id][0].$data.renderCount++;
                this.$refs['fileLink' + file.id][0].$forceUpdate()
            }
        },
        download(file) {
            window.open(this.downloadUrl(file.id));
        },
        async updateFiles() {
            let data = await this.currentFiles()
            data.forEach((file, index) => {
                let i = this.files.find((inner) => inner.id === file.id)
                let render = false;
                if (i) {
                    if (i.status !== 'uploaded') {
                        i.status = 'uploaded'
                        i.thumbnail = file.thumbnail
                        render = true
                    }
                } else {
                    file.status = 'uploaded'
                    this.files.push(file)
                    render = true
                }
                if (render) {
                    this.$nextTick(() => {
                        this.reRenderFile(file)
                    })
                }
            })
        },
        async updateFromListener(e) {
            await this.updateFiles()
            let i = this.files.find((inner) => inner.id === e.data.fileId)
            if (i) {
                this.$nextTick(() => {
                    this.reRenderFile(i)
                })
            }
        },
    },
    watch: {},
    async mounted() {
        if (!this.readonly && this.filesystemId) {
            User.registerAggregate({
                "type": 'Filesystem',
                'id': this.filesystemId,
                'event': 'ThumbnailGenerated'
            }, this.updateFromListener, true)
        }
        await this.updateFiles();
        if (!this.readonly) {
            this.$refs.dropper.ondragover = (evt) => {
                this.dropHovering = true;
                evt.preventDefault();
            };
            this.$refs.dropper.ondragend = (evt) => {
                this.dropHovering = false;
                evt.preventDefault();
            };
            this.$refs.dropper.ondragleave = (evt) => {
                this.dropHovering = false;
                evt.preventDefault();
            };
            this.$refs.dropper.ondragenter = (evt) => {
                evt.preventDefault();
            };
            this.$refs.dropper.ondrop = (evt) => {
                this.dropHovering = false;
                evt.target.files = evt.dataTransfer.files;
                evt.preventDefault();
                this.onFilesChange(evt)
            };
        }
    }
}
</script>
